'use client';

import { useCallback, useMemo, useState } from 'react';
import { BigNum, QUOTE_PRECISION_EXP } from '@drift-labs/sdk';
import { UIMarket } from '@drift/common';
import Tabs from 'src/components/primitives/Tabs/Tabs';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import { AssetFilterWithSearch } from 'src/components/Inputs/AssetFilterWithSearch';
import { AssetListAssetWithMarketIndex } from 'src/@types/assets';
import useModal from 'src/components/Modals/hooks/useModal';
import useAccountData from 'src/hooks/useAccountData';
import { useFundingPaymentsPerformance } from 'src/hooks/performance/useFundingPaymentsPerformance';
import { usePerpMarkets } from 'src/hooks/perpMarkets/usePerpMarkets';
import ResponsiveModal from './ResponsiveModal/ResponsiveModal';
import { PortfolioPeriod } from 'src/components/PortfolioPage/types';
import {
	PORTFOLIO_DEFAULT_PERIOD,
	PORTFOLIO_PERIOD_TAB_OPTIONS,
} from 'src/components/PortfolioPage/consts';
import PnlChart from '../PnlChart';
import useLatchedValue from 'src/hooks/utils/useLatchedValue';
import { cj } from 'src/utils/classNames';
import computePortfolioTradeDelta from 'src/components/PortfolioPage/utils/computePortfolioTradeDelta';
import { LoadingChart } from 'src/components/Charts/LoadingChart';

const portfolioPeriodToPerformanceKey = (
	period: PortfolioPeriod
): 'all' | '24h' | '7d' | '30d' => {
	if (period === '90d') return 'all';
	return period;
};

export interface NetFundingHistoryModalProps {
	defaultPeriod?: PortfolioPeriod;
}

export default function NetFundingHistoryModal({
	defaultPeriod,
}: NetFundingHistoryModalProps) {
	const { hide } = useModal();
	const currentAccountPubKey = useAccountData()?.pubKey;
	const currentPerpMarkets = usePerpMarkets();

	const [selectedPeriodFromState, setSelectedPeriod] = useState(
		defaultPeriod ?? PORTFOLIO_DEFAULT_PERIOD
	);
	const [selectedMarketFromState, setSelectedMarket] = useState<
		number | 'all_markets'
	>('all_markets');

	const marketOptions: AssetListAssetWithMarketIndex[] = useMemo(
		() => [
			{
				displayLabel: 'All Markets',
				marketIndex: -1,
				ticker: 'all_markets',
			},
			...currentPerpMarkets
				.filter((market) => !UIMarket.checkIsPredictionMarket(market))
				.map((mkt) => ({
					ticker: mkt.marketIndex.toString(),
					displayLabel: UIMarket.createPerpMarket(mkt.marketIndex).marketName,
					marketIndex: mkt.marketIndex,
				})),
		],
		[currentPerpMarkets]
	);

	const {
		data: marketPerformanceData,
		isLoading,
		isPlaceholderData,
	} = useFundingPaymentsPerformance({
		userAccount: currentAccountPubKey?.toString(),
		period: selectedPeriodFromState,
		marketFilter: selectedMarketFromState,
	});

	const handleMarketChange = useCallback((newMarket: string) => {
		setSelectedMarket(
			newMarket === 'all_markets' ? 'all_markets' : Number(newMarket)
		);
	}, []);

	const selectedPeriod = useLatchedValue({
		value: selectedPeriodFromState,
		isLatched: isPlaceholderData,
	});

	const selectedMarket = useLatchedValue({
		value: selectedMarketFromState,
		isLatched: isPlaceholderData,
	});

	const chartData = useMemo(() => {
		const perfData = marketPerformanceData?.find(
			(mkt) => mkt.marketFilter === selectedMarket
		);

		const performanceKey = portfolioPeriodToPerformanceKey(selectedPeriod);
		return perfData?.performance[performanceKey] ?? [];
	}, [selectedMarket, marketPerformanceData, selectedPeriod]);

	const totalFunding = useMemo(() => {
		if (!currentAccountPubKey) {
			return;
		}

		if (!chartData?.length) {
			return BigNum.zero();
		}

		const value = computePortfolioTradeDelta(
			chartData[chartData.length - 1]?.totalPnl,
			chartData[0]?.totalPnl
		);

		return BigNum.fromPrint(value.toString(), QUOTE_PRECISION_EXP);
	}, [chartData, currentAccountPubKey]);

	return (
		<ResponsiveModal.Container
			onClose={hide}
			className="sm:w-[632px]"
			dataTestId="net-funding-history-modal"
		>
			<ResponsiveModal.Title closeButtonTestId="net-funding-history-modal-close">
				Net Funding
			</ResponsiveModal.Title>

			<ResponsiveModal.Body className="gap-y-md">
				<div className="flex flex-col justify-between gap-y-lg text-text-default sm:flex-row sm:items-center">
					<NumberDisplay
						value={totalFunding}
						type="currency"
						className={cj('typo-t1', isPlaceholderData && 'animate-pulse')}
						skeletonClassName="h-5 sm:h-8 w-32"
						loading={isLoading}
						signDependentColor
					/>

					<div className="flex flex-col gap-x-sm gap-y-lg sm:flex-row sm:items-center sm:gap-y-md">
						<AssetFilterWithSearch
							id="net-funding-market-filter"
							label="Market"
							className="w-full sm:w-[220px]"
							scrollContainerClassName="max-h-52"
							currentSelection={String(selectedMarketFromState)}
							options={marketOptions}
							onChange={handleMarketChange}
						/>
						<Tabs
							size="sm"
							variant="secondary"
							options={PORTFOLIO_PERIOD_TAB_OPTIONS}
							activeTab={selectedPeriodFromState}
							onTabChange={setSelectedPeriod}
							className="w-fit"
						/>
					</div>
				</div>

				<div className={cj('h-80', isPlaceholderData && 'animate-pulse')}>
					{isLoading ? (
						<LoadingChart className="h-full" />
					) : (
						<PnlChart
							id="net-funding-history"
							data={chartData}
							field="totalPnl"
							label="Funding Earned"
							positive={false}
							decimalsToShow={2}
							isInModal
							isHourly={selectedPeriod === '24h'}
						/>
					)}
				</div>
			</ResponsiveModal.Body>
		</ResponsiveModal.Container>
	);
}
